/**
 * Fait : la recherche d'un modèle chez un fournisseur qui en sert plusieurs
 *   centaines. On écrit deux lettres, on lance, on choisit dans les résultats —
 *   et l'identifiant comme les DEUX PRIX partent remplir le formulaire, déjà
 *   convertis en euros (`D-036`).
 *
 *   POURQUOI, dans les mots de Charles le 2026-08-26 : « je choisis par exemple
 *   open routeur et c'est pas clair on va où on fait quoi comment ». Il fallait
 *   connaître par cœur un identifiant qu'aucun écran ne montrait, puis recopier
 *   deux prix publiés en dollars par jeton. Trois occasions de se tromper, dont
 *   deux qui font compter le budget faux sans que rien ne le dise.
 *
 *   LA DATE DU COURS EST AFFICHÉE, et ce n'est pas un détail : les prix
 *   viennent d'être convertis depuis le dollar au taux de la Banque centrale
 *   européenne, qui ne publie pas le week-end. Montrer la date, c'est dire de
 *   quand datent ces euros — plutôt que de laisser croire à un relevé de
 *   l'instant.
 *
 *   ELLE NE PROPOSE RIEN QUAND ELLE NE SAIT PAS. Si le fournisseur ou le cours
 *   est injoignable, le serveur refuse et le dit ; cet écran répète le refus au
 *   lieu d'offrir des prix devinés.
 *
 * Ne fait pas : elle n'inscrit rien au catalogue — elle REMPLIT le formulaire,
 *   qui reste le geste — et n'interroge aucun tiers elle-même : c'est le
 *   serveur qui sort, jamais le navigateur.
 */

import { useState } from 'react';

import { Annonce } from '../gabarits/Annonce.tsx';
import { Champ } from '../gabarits/Champ.tsx';
import { chercheDesModelesProposes, type ModeleProposeVu } from './api-argent.ts';
import { enEuros } from './montants.ts';

/** Ce que l'écran est en train de faire. */
type Etat = 'repos' | 'en_cours' | 'pret';

export function RechercheDeModele({
  surChoix,
}: {
  /** Ce qu'on fait du modèle choisi : remplir le formulaire qui inscrit. */
  readonly surChoix: (modele: ModeleProposeVu) => void;
}) {
  const [terme, setTerme] = useState('');
  const [etat, setEtat] = useState<Etat>('repos');
  const [trouves, setTrouves] = useState<ModeleProposeVu[]>([]);
  const [combien, setCombien] = useState(0);
  const [coursDuLe, setCoursDuLe] = useState<string | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  async function cherche(): Promise<void> {
    setEtat('en_cours');
    setRefus(null);

    const issue = await chercheDesModelesProposes(terme);

    if (!issue.bon) {
      setEtat('repos');
      setTrouves([]);
      setRefus(issue.erreur.message);
      return;
    }

    setTrouves(issue.valeur.modeles);
    setCombien(issue.valeur.combienEnTout);
    setCoursDuLe(issue.valeur.coursDuLe);
    setEtat('pret');
  }

  return (
    <div data-role="recherche-de-modele">
      <Champ
        libelle="Chercher un modèle chez ce fournisseur"
        aide="Deux lettres suffisent — par exemple « sonnet », « gpt » ou « deepseek »."
        type="search"
        name="terme-du-modele"
        data-role="terme-du-modele"
        value={terme}
        onChange={(evenement) => setTerme(evenement.target.value)}
        onKeyDown={(evenement) => {
          /* Entrée cherche, et n'envoie PAS le formulaire qui nous contient :
             sans cela, chercher inscrivait un modèle à moitié rempli. */
          if (evenement.key === 'Enter') {
            evenement.preventDefault();
            void cherche();
          }
        }}
      />

      <div className="barre-de-boutons">
        <button
          type="button"
          data-role="lancer-la-recherche-de-modele"
          disabled={etat === 'en_cours' || terme.trim().length < 2}
          onClick={() => void cherche()}
        >
          {etat === 'en_cours' ? 'Recherche…' : 'Chercher'}
        </button>
      </div>

      {refus !== null && (
        <Annonce urgence="urgent" nature="refus" data-role="refus-de-la-recherche">
          {refus}
        </Annonce>
      )}

      {etat === 'pret' && trouves.length === 0 && (
        <p data-etat="vide" data-role="aucun-modele-trouve">
          Aucun modèle ne porte « {terme.trim()} » chez ce fournisseur.
        </p>
      )}

      {etat === 'pret' && trouves.length > 0 && (
        <>
          <p className="texte-attenue" data-role="cours-du-change">
            Prix convertis en euros au cours de la Banque centrale européenne du {coursDuLe}.
            {combien > trouves.length
              ? ` ${String(trouves.length)} modèles montrés sur ${String(combien)} trouvés —
                 précisez votre recherche pour voir les autres.`
              : ''}
          </p>

          <ul className="empilement" data-liste="modeles-proposes">
            {trouves.map((modele) => (
              <li key={modele.identifiantModele} data-role="modele-propose">
                <h5>{modele.nom}</h5>
                <p className="texte-attenue">{modele.identifiantModele}</p>
                <p>
                  {enEuros(modele.prixEntreeCentimesParMillionDeJetons)} par million de jetons
                  envoyés, {enEuros(modele.prixSortieCentimesParMillionDeJetons)} par million reçus.
                </p>

                <div className="barre-de-boutons">
                  <button
                    type="button"
                    data-role="choisir-le-modele-propose"
                    data-modele={modele.identifiantModele}
                    onClick={() => surChoix(modele)}
                  >
                    Choisir celui-ci
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
